import { z, type ZodObject, type ZodType, type ZodTypeAny } from "zod";
import type { BulkFormData, FormStructure } from "~/types/ui";

const HEX_INPUT_OPTIONS_KEY: string = "HEX_INPUT_OPTIONS_KEY";

export function zodSchemaFromStructure<T>(
  structure: FormStructure<T>,
  t?: (key: string, options?: Record<string, unknown>) => string,
): ZodTypeAny {
  const shape: Record<string, ZodType<any>> = {};
  const functionRules: Array<{
    key: string;
    rule: (value: any, data: T) => true | string;
  }> = [];
  const requiredFields: Array<{ key: string; label: string }> = [];

  for (const key in structure) {
    const def = structure[key as keyof FormStructure<T>];
    if (!def || key === "virtualFields") continue;

    if (def.required) requiredFields.push({ key, label: def.label });

    if (def.rules instanceof z.ZodType) {
      shape[key] = def.rules; // use provided zod rule
    } else {
      if (typeof def.rules === "function") {
        functionRules.push({
          key,
          rule: def.rules,
        });
      }
      // default to a permissive schema if none defined
      shape[key] = z.any();
    }
  }

  return z.object(shape).superRefine((data, ctx) => {
    const typedData = data as T;

    for (const { key, label } of requiredFields) {
      const value = (typedData as Record<string, unknown>)[key];
      const isEmpty =
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0);

      if (isEmpty) {
        ctx.addIssue({
          path: [key],
          code: z.ZodIssueCode.custom,
          message: t
            ? t("common.fieldRequired", { field: t(label) })
            : `${label} is required`,
        });
      }
    }

    for (const { key, rule } of functionRules) {
      const result = rule(typedData[key as keyof T], typedData);

      if (result !== true) {
        ctx.addIssue({
          path: [key],
          code: z.ZodIssueCode.custom,
          message: result,
        });
      }
    }
  });
}

export const hexInputOptions = (
  byteLength: number,
): { value: number; label: string }[] => {
  return [
    {
      label: HEX_INPUT_OPTIONS_KEY,
      value: byteLength,
    },
  ];
};

export const getHexInputOptions = (options: any[]): number => {
  const res = options.find((o) => o.label === HEX_INPUT_OPTIONS_KEY);
  if (!res) return 16;

  return res.value;
};

export function isBulkFormData<T>(d: any): d is BulkFormData<T> {
  return d && typeof d === "object" && "items" in d;
}

export function getRerenderFieldNames<T>(
  structure: FormStructure<T>,
): string[] {
  const rerenderNames: string[] = [];
  for (const key in structure) {
    const def = structure[key as keyof FormStructure<T>];
    if (def.rerender !== undefined && def.rerender === true)
      rerenderNames.push(key);
  }

  return rerenderNames;
}
